/**
 * Main App Component
 * Sets up routing and context
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { connectSocket, getSocket } from './services/socket';
import { useSessionTimeout } from './hooks/useSessionTimeout';
import GameNotifications from './components/GameNotifications';

// Pages
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import VerifyEmailSent from './pages/VerifyEmailSent';
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';
import Register from './pages/Register';
import TermsAndConditions from './pages/TermsAndConditions';
import Dashboard from './pages/Dashboard';
import Dashboard1 from './pages/Dashboard1';
import GameGuide from './pages/GameGuide';
import Game from './pages/Game';
import Leaderboard from './pages/Leaderboard';
import Challenges from './pages/Challenges';
import ChallengeGame from './pages/ChallengeGame';
import SeriesComplete from './pages/SeriesComplete';
import History from './pages/History';
import WhotResults from './pages/WhotResults';
import Ledger from './pages/Ledger';
import FlappyLobby from './pages/FlappyLobby';
import FlappyMatch from './pages/FlappyMatch';
import FlappySolo from './pages/FlappySolo';
import FlappySimMatch from './pages/FlappySimMatch';

import ReactionSolo from './pages/ReactionSolo';
import ReactionLobby from './pages/ReactionLobby';
import ReactionMatch from './pages/ReactionMatch';

import ChessPage from './pages/Chess';
import ChessLobby from './pages/ChessLobby';
import ChessMatch from './pages/ChessMatch';
import ChessTournamentLobby from './pages/ChessTournamentLobby';
import ChessTournamentDetails from './pages/ChessTournamentDetails';
import CheckersLobby from './pages/CheckersLobby';
import CheckersMatch from './pages/CheckersMatch';
import CheckersPractice from './pages/CheckersPractice';
import DraughtsLobby from './pages/DraughtsLobby';
import DraughtsMatch from './pages/DraughtsMatch';
import DraughtsPractice from './pages/DraughtsPractice';

import WhotCardsPreview from './pages/WhotCardsPreview';
import WhotOnlineLobby from './pages/WhotOnlineLobby';
import WhotOnlineRoom from './pages/WhotOnlineRoom';
import WhotAnonymous from './pages/WhotAnonymous';
import WhotTournamentLobby from './pages/WhotTournamentLobby';
import WhotTournamentDetails from './pages/WhotTournamentDetails';
import Rewards from './pages/Rewards';
import ReferralDashboard from './pages/ReferralDashboard';

// Multiplayer Components
import MultiplayerLobby from './components/MultiplayerLobby';
import MultiplayerGame from './components/MultiplayerGame';

// Components
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import AppShell from './components/AppShell';

// Styles
import './App.css';

function AppContent() {
  const { user, loading, token } = useAuth();
  const [socket, setSocket] = useState(null);
  const location = useLocation();
  const backgroundLocation = location.state && location.state.backgroundLocation;
  
  // Session timeout management
  useSessionTimeout();

  // Connect socket when user is authenticated
  useEffect(() => {
    if (user && token) {
      connectSocket(user.id, token);
      setSocket(getSocket());
    }

    return () => {
      // Don't disconnect here - we'll handle it on logout
    };
  }, [user, token]);

  // Global listener: when a direct challenge is accepted (series started),
  // auto-navigate players into the correct UI so both clients join the room.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;

    const onChallengeStarted = (challenge) => {
      if (!challenge || !challenge.id) return;
      const game = (challenge.game || '').toLowerCase();

      // If it's a Whot challenge, open the Whot room so the client will join and
      // receive the server's seeded `whot:start` (deterministic multiplayer start).
      if (game === 'whot') {
        window.history.replaceState({}, '', `/whot-online/room/${challenge.id}`);
        // Use navigation from react-router only inside components — trigger via location change
        // so the router will mount the page. (This keeps behaviour simple and reliable.)
        window.dispatchEvent(new PopStateEvent('popstate'));
        return;
      }

      // Fallback — navigate to the challenge game page
      window.history.replaceState({}, '', `/challenges/${challenge.id}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    };

    const onChessArenaPaired = (data) => {
      if (!data || !data.matchId) return;
      window.history.replaceState({}, '', `/chess/duel/${data.matchId}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    };

    const onArenaPaired = (data) => {
      if (!data || !data.roomId) return;
      window.history.replaceState({}, '', `/whot-online/room/${data.roomId}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
    };

    socket.on('challenge:started', onChallengeStarted);
    socket.on('chess:arena_paired', onChessArenaPaired);
    socket.on('whot:arena_paired', onArenaPaired);
    return () => {
      socket.off('challenge:started', onChallengeStarted);
      socket.off('chess:arena_paired', onChessArenaPaired);
      socket.off('whot:arena_paired', onArenaPaired);
    };
  }, [user, token]);

  if (loading) {
    return <div className="loading-screen">Loading...</div>;
  }

  return (
    <>
      <Toaster position="top-right" />
      <GameNotifications />
      <Routes location={backgroundLocation || location}>
      {/* Public Routes — if user is already logged in redirect to their intended destination or dashboard */}
      <Route path="/login" element={<Login />} />
      <Route path="/forgot-password" element={!user ? <ForgotPassword /> : <Navigate to="/dashboard" replace />} />
      <Route path="/reset-password" element={!user ? <ResetPassword /> : <Navigate to="/dashboard" replace />} />
      <Route path="/verify-email" element={!user ? <VerifyEmail /> : <Navigate to="/dashboard" replace />} />
      <Route path="/verify-email-sent" element={!user ? <VerifyEmailSent /> : <Navigate to="/dashboard" replace />} />
      <Route path="/admin/login" element={!user ? <AdminLogin /> : <Navigate to={user?.role === 'admin' ? '/admin' : '/dashboard'} replace />} />
      <Route path="/register" element={<Register />} />
      <Route path="/terms" element={<TermsAndConditions />} />

      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppShell>
              <Dashboard />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/dashboard1"
        element={
          <ProtectedRoute>
            <AppShell>
              <Dashboard1 />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/guide"
        element={user ? (
          <ProtectedRoute>
            <AppShell>
              <GameGuide />
            </AppShell>
          </ProtectedRoute>
        ) : (
          <GameGuide />
        )}
      />

      <Route
        path="/game/:gameId"
        element={
          <ProtectedRoute>
            <AppShell>
              <Game />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/challenges"
        element={
          <ProtectedRoute>
            <AppShell>
              <Challenges />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/challenges/:challengeId"
        element={
          <ProtectedRoute>
            <AppShell>
              <ChallengeGame />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/series-complete/:challengeId"
        element={
          <ProtectedRoute>
            <AppShell>
              <SeriesComplete />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/leaderboard"
        element={<Leaderboard />}
      />

      <Route
        path="/history"
        element={
          <ProtectedRoute>
            <AppShell>
              <History />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/whot/results/:challengeId"
        element={
          <ProtectedRoute>
            <AppShell>
              <WhotResults />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ledger"
        element={
          <ProtectedRoute>
            <AppShell>
              <Ledger />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/rewards"
        element={
          <ProtectedRoute>
            <AppShell>
              <Rewards />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/referrals"
        element={
          <ProtectedRoute>
            <AppShell>
              <ReferralDashboard />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/multiplayer"
        element={
          <ProtectedRoute>
            <AppShell>
              <MultiplayerLobby user={user} socket={socket} />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/multiplayer/game/:challengeId"
        element={
          <ProtectedRoute>
            <AppShell>
              <MultiplayerGame user={user} socket={socket} />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/flappy"
        element={
          <ProtectedRoute>
              <AppShell>
                <FlappyLobby />
              </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reaction"
        element={
          <ProtectedRoute>
            <AppShell>
              <ReactionLobby />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reaction/practice"
        element={
          <ProtectedRoute>
            <AppShell>
              <ReactionSolo />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/reaction/duel"
        element={<Navigate to="/reaction" replace />}
      />

      <Route
        path="/reaction/duel/:matchId"
        element={
          <ProtectedRoute>
            <AppShell>
              <ReactionMatch />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/chess"
        element={
          <ProtectedRoute>
            <AppShell>
              <ChessLobby />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/chess/practice"
        element={
          <ProtectedRoute>
            <AppShell>
              <ChessPage />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/chess/duel"
        element={
          <ProtectedRoute>
            <AppShell>
              <ChessLobby />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/chess/duel/:matchId"
        element={
          <ProtectedRoute>
            <AppShell fullBleed>
              <ChessMatch />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/chess/tournaments"
        element={
          <ProtectedRoute>
            <AppShell>
              <ChessTournamentLobby />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/chess/tournaments/:tournamentId"
        element={
          <ProtectedRoute>
            <AppShell>
              <ChessTournamentDetails />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/checkers"
        element={<Navigate to="/checkers/lobby" replace />}
      />

      <Route
        path="/checkers/lobby"
        element={
          <ProtectedRoute>
            <AppShell>
              <CheckersLobby />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/checkers/practice"
        element={
          <ProtectedRoute>
            <AppShell>
              <CheckersPractice />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/checkers/duel/:matchId"
        element={
          <ProtectedRoute>
            <AppShell>
              <CheckersMatch />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/draughts"
        element={<Navigate to="/draughts/lobby" replace />}
      />

      <Route
        path="/draughts/lobby"
        element={
          <ProtectedRoute>
            <AppShell>
              <DraughtsLobby />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/draughts/practice"
        element={
          <ProtectedRoute>
            <AppShell>
              <DraughtsPractice />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/draughts/duel/:matchId"
        element={
          <ProtectedRoute>
            <AppShell>
              <DraughtsMatch />
            </AppShell>
          </ProtectedRoute>
        }
      />


      <Route
        path="/whot-online"
        element={
          <ProtectedRoute>
            <AppShell>
              <WhotOnlineLobby />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route path="/newlobby" element={<Navigate to="/whot-online" replace />} />

      <Route
        path="/whot/tournaments"
        element={
          <ProtectedRoute>
            <AppShell>
              <WhotTournamentLobby />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/whot/tournaments/:tournamentId"
        element={
          <ProtectedRoute>
            <AppShell>
              <WhotTournamentDetails />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/whot-online/preview"
        element={
          <ProtectedRoute>
            <AppShell>
              <WhotCardsPreview />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/whot/practice"
        element={<WhotAnonymous />}
      />

      <Route
        path="/whot-online/room/:roomId"
        element={
          <ProtectedRoute>
            <AppShell hideMenu fullBleed>
              <WhotOnlineRoom />
            </AppShell>
          </ProtectedRoute>
        }
      />

      <Route
        path="/flappy/practice"
        element={<FlappySolo />}
      />

      <Route
        path="/flappy/solo"
        element={<Navigate to="/flappy/practice" />}
      />

      <Route
        path="/flappy/duel"
        element={
          <ProtectedRoute>
            <AppShell>
              <FlappyLobby />
            </AppShell>
          </ProtectedRoute>
        }
      />

        <Route
          path="/flappy/duel/:matchId"
        element={
          <ProtectedRoute>
              <AppShell hideMenu fullBleed>
              <FlappyMatch />
            </AppShell>
          </ProtectedRoute>
        }
      />

        <Route
            path="/flappy/sim/:matchId"
          element={
            <ProtectedRoute>
                <AppShell>
                  <FlappySimMatch />
                </AppShell>
            </ProtectedRoute>
          }
        />

      {/* Public landing dashboard */}
      <Route
        path="/"
        element={
          <AppShell>
            <Dashboard />
          </AppShell>
        }
      />
      </Routes>

      {backgroundLocation && !user && (
        <Routes>
          <Route path="/login" element={<Login />} />
        </Routes>
      )}
    </>
  );
}


function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
